import React, { useCallback, useMemo } from 'react';
import { useLayoutContext } from 'context/LayoutContext';

const useValidateDateGridComponent = (
  {
    cell,
    onDoubleClick,
    selected,
    editing,
    children,
    value,
  }: {
    cell: Record<string, any>;
    onDoubleClick: (args: any) => void;
    selected: boolean;
    editing: boolean;
    children: React.ReactNode;
    value: any;
  },
  doubleClicked: boolean,
  setDoubleClicked: (value: boolean) => void
) => {
  const DatePicker = cell.gridDateComponent || React.Fragment;
  const { isExportMode } = useLayoutContext();

  const { minDate, maxDate } = cell;

  const doubleClickWrapper = useCallback(
    args => {
      setDoubleClicked(true);
      onDoubleClick(args);
    },
    [onDoubleClick, setDoubleClicked]
  );

  const doubleClickEvent = useMemo(() => {
    if (cell.gridDateComponent && !cell.readOnly && !editing && !isExportMode) {
      return {};
    }
    return { onDoubleClick: doubleClickWrapper, onTouchEnd: onDoubleClick };
  }, [cell.gridDateComponent, cell.readOnly, editing, isExportMode, doubleClickWrapper, onDoubleClick]);

  const cellContent = useMemo(() => {
    if (cell.gridDateComponent && !cell.readOnly && !editing && !isExportMode) {
      return (
        <>
          <div onDoubleClick={onDoubleClick} onTouchEnd={onDoubleClick}>
            {React.cloneElement(children as React.ReactElement, { selected, onDoubleClick, value }, null)}
          </div>
          {cell.gridDateComponent && !cell.readOnly && <DatePicker cell={cell} minDate={minDate} maxDate={maxDate} />}
        </>
      );
    }
    return React.cloneElement(
      children as React.ReactElement,
      {
        selected,
        onDoubleClick,
        doubleClicked,
        setDoubleClicked,
        value,
      },
      null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell, editing, isExportMode, children, selected, onDoubleClick, setDoubleClicked, value, minDate, maxDate]);

  return { doubleClickEvent, cellContent };
};

export default useValidateDateGridComponent;
